import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { foraDaProva } from '@/features/relatos/podeRelatar';
import { api } from '@/utils/api';

/**
 * A CENTRAL DE NOTIFICAÇÕES (19/10/2026) — o cliente das rotas `/api/notificacoes` (servidor: `services/notificacoes.ts`).
 * O contador atualiza ao trocar de tela (quem chama passa o caminho na chave) e a cada 60 s com a aba visível.
 */
export type TipoDeNotificacao =
  | 'ENTREGAS' | 'CORRECAO_PENDENTE' | 'FECHA_SEM_ENTRAR' | 'SIMULADO_ENCERRADO' | 'SALA_SEM_ENVIO' | 'PDF_PRONTO'
  | 'CALIBRACAO' | 'BASE_DESATUALIZADA' | 'ROTINA_FALHOU' | 'SIMULADO_ABERTO' | 'PERTO_DE_FECHAR' | 'RESULTADO_LIBERADO'
  | 'CONFERIR_GABARITO' | 'MANUTENCAO' | 'ENTREGA_INCOMPLETA';

export interface Notificacao {
  id: number;
  tipo: TipoDeNotificacao;
  titulo: string;
  corpo: string;
  link: string | null;
  criadaEm: string;
  atualizadaEm: string;
  lidaEm: string | null;
  /**
   * Nos avisos do ALUNO sobre uma prova (prova nova, fecha em 24 h, resultado): a situação de AGORA, calculada na leitura
   * (27/10/2026). Nula = a prova está aberta e ele ainda não entregou. Ausente no servidor de antes.
   */
  situacao?: 'Corrigida' | 'Entregue' | 'Encerrada' | null;
  /** Nos avisos de simulado. */
  simulationId?: number | null;
  /** Nos avisos de entregas e de correção (`services/alunosNosAvisos.ts`): os nomes montados na hora, pelas guardas. */
  lista?: 'entregas' | 'pendentes' | 'incompletas';
  /** Nas incompletas, também "N de M respondidas" e se foi entregue pelo portal (tempo esgotado). */
  alunos?: Array<{ nome: string; turma: string | null; respondidas?: number; de?: number; automatica?: boolean }>;
  mais?: number;
  /** Links a mais: as questões do "Vale conferir o gabarito", o relatório por habilidade no fechamento. */
  links?: Array<{ texto: string; href: string }>;
  /** O PDF pedido (25/10/2026): gerando → pronto (arquivos, validade) ou falhou; vencido, "Gerar de novo". */
  pdf?: { estado: 'gerando' | 'pronto' | 'falhou'; arquivos: Array<{ url: string; nome: string }>; validoAte: string | null; desde: string | null; gerarDeNovo: string | null };
}

export interface ListaCompleta {
  simulationId: number;
  lista: 'entregas' | 'pendentes' | 'incompletas';
  total: number;
  grupos: Array<{ turma: string; alunos: Array<{ nome: string; em: string; respondidas?: number; de?: number; automatica?: boolean }> }>;
  linkAcompanhar: string;
}

export interface Preferencias {
  /** `nome` e `descricao` vêm do servidor (19/10/2026): a tela os usa quando não conhece o tipo. */
  tipos: Array<{ tipo: TipoDeNotificacao; ligado: boolean; nome?: string; descricao?: string }>;
  silencio: { inicioMin: number; fimMin: number; fuso: string };
  acompanharSimuladosDeProfessores?: boolean;
}

/** O que cada tipo quer dizer, na tela de preferências. */
export const ROTULO_DO_TIPO: Record<TipoDeNotificacao, string> = {
  ENTREGAS: 'Entregas nos meus simulados',
  CORRECAO_PENDENTE: 'Respostas discursivas esperando correção',
  FECHA_SEM_ENTRAR: 'Simulado que fecha em 24 h com alunos que nunca entraram',
  SIMULADO_ENCERRADO: 'Resumo quando um simulado fecha',
  SALA_SEM_ENVIO: 'Sala sem internet sem resultados enviados',
  PDF_PRONTO: 'PDF pronto (ou que não saiu)',
  CALIBRACAO: 'Calibração de Rasch concluída ou interrompida',
  BASE_DESATUALIZADA: 'Base de Rasch desatualizada',
  ROTINA_FALHOU: 'Falha em rotina do servidor',
  SIMULADO_ABERTO: 'Prova nova aberta para mim',
  PERTO_DE_FECHAR: 'Prova que fecha em 24 h e eu ainda não entreguei',
  RESULTADO_LIBERADO: 'Resultado liberado',
  CONFERIR_GABARITO: 'Questão com sinal de gabarito a conferir (a partir de 30 entregas e no fechamento)',
  MANUTENCAO: 'Manutenção do servidor (disco, backup, erros, conexões, certificados)',
  ENTREGA_INCOMPLETA: 'Entregas com menos da metade das questões respondidas',
};

export const CHAVE_DA_CONTAGEM = ['notificacoes', 'contagem'] as const;

/** O pedido da contagem — um só, para o sino, as abas do aluno, a página e o aviso do aplicativo de computador. */
export const pedirContagem = async () => (await api.get<{ naoLidas: number }>('/api/notificacoes/contagem')).data.naoLidas;

/**
 * A CONTAGEM DO SINO NÃO É REFEITA A CADA NAVEGAÇÃO (25/10/2026, desempenho). A chave levava o caminho da página — cada
 * tela nova era um pedido novo, ao lado das consultas da própria tela. Agora a chave é uma só, e a contagem vale 30 s;
 * é refeita a cada 60 s com a janela à frente, ao voltar à janela, quando a pessoa lê (`useMarcarComoLida`,
 * `useMarcarTodasComoLidas` invalidam a chave) e ao SAIR da página da prova (o aluno não vê avisos durante a prova, e o que
 * chegou nela aparece na saída).
 */
export function useContagemDeNaoLidas(ativo: boolean, caminho: string) {
  const qc = useQueryClient();
  const naProva = useRef(!foraDaProva(caminho));
  useEffect(() => {
    const agoraNaProva = !foraDaProva(caminho);
    if (naProva.current && !agoraNaProva) void qc.invalidateQueries({ queryKey: CHAVE_DA_CONTAGEM });
    naProva.current = agoraNaProva;
  }, [caminho, qc]);
  return useQuery<number>({
    queryKey: [...CHAVE_DA_CONTAGEM],
    queryFn: pedirContagem,
    enabled: ativo,
    refetchInterval: () => (typeof document !== 'undefined' && document.visibilityState === 'visible' ? 60_000 : false),
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

export function useNotificacoes(soNaoLidas: boolean) {
  return useQuery<Notificacao[]>({
    queryKey: ['notificacoes', 'lista', soNaoLidas ? 'nao-lidas' : 'todas'],
    queryFn: async () => (await api.get<Notificacao[]>('/api/notificacoes', { params: { status: soNaoLidas ? 'nao-lidas' : 'todas', limite: 50 } })).data,
    refetchOnWindowFocus: true,
  });
}

/** A lista completa de um aviso (o toque em "e mais N"): só busca quando aberta. */
export function useListaCompleta(id: number, aberta: boolean) {
  return useQuery<ListaCompleta>({
    queryKey: ['notificacoes', 'alunos', id],
    queryFn: async () => (await api.get<ListaCompleta>(`/api/notificacoes/${id}/alunos`)).data,
    enabled: aberta,
  });
}

/** Depois de marcar: a lista e o contador relidos. */
function useRecarregar() {
  const qc = useQueryClient();
  return () => { void qc.invalidateQueries({ queryKey: ['notificacoes'] }); };
}

/**
 * Marcar UM como lido não relê a lista: o aviso fica à vista (lido) até a próxima leitura — senão, na aba "Não lidas", o
 * aviso tocado sumia junto com a lista de quem entregou que o toque acabou de abrir. Só o contador é relido.
 */
export function useMarcarComoLida() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/api/notificacoes/${id}/lida`),
    onSuccess: (_r, id) => {
      const agora = new Date().toISOString();
      qc.setQueriesData<Notificacao[]>({ queryKey: ['notificacoes', 'lista'] }, (lista) => lista?.map((n) => (n.id === id && !n.lidaEm ? { ...n, lidaEm: agora } : n)));
      void qc.invalidateQueries({ queryKey: CHAVE_DA_CONTAGEM });
    },
  });
}

export function useMarcarTodasComoLidas() {
  const recarregar = useRecarregar();
  return useMutation({ mutationFn: () => api.post('/api/notificacoes/lidas'), onSuccess: recarregar });
}

export function usePreferencias(ativo = true) {
  return useQuery<Preferencias>({ queryKey: ['notificacoes', 'preferencias'], queryFn: async () => (await api.get<Preferencias>('/api/notificacoes/preferencias')).data, enabled: ativo });
}

export function useGravarPreferencias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (corpo: Partial<{ tipos: Record<string, boolean>; silencio: { inicioMin?: number; fimMin?: number }; acompanharSimuladosDeProfessores: boolean }>) =>
      (await api.put<Preferencias>('/api/notificacoes/preferencias', corpo)).data,
    onSuccess: (dados) => qc.setQueryData(['notificacoes', 'preferencias'], dados),
  });
}
